// src/context/ThemeContext.js

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const ThemeContext = createContext({
  theme: 'light',
  setTheme: () => {},
  toggleTheme: () => {},
});

function getSystemPref() {
  if (
    typeof window === 'undefined' ||
    !window.matchMedia
  ) {
    return 'light';
  }

  return window.matchMedia(
    '(prefers-color-scheme: dark)'
  ).matches
    ? 'dark'
    : 'light';
}

function applyThemeAttr(theme) {
  if (typeof document === 'undefined') return;

  document.documentElement.setAttribute(
    'data-theme',
    theme
  );
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === 'undefined') {
      return 'light';
    }

    return (
      localStorage.getItem('barterfi:theme') ||
      getSystemPref()
    );
  });

  useEffect(() => {
    applyThemeAttr(theme);
  }, [theme]);

  useEffect(() => {
    const savedTheme = localStorage.getItem(
      'barterfi:theme'
    );

    // Only follow the device theme when the user
    // has not manually selected a theme.
    if (savedTheme || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(
      '(prefers-color-scheme: dark)'
    );

    const handleSystemThemeChange = (event) => {
      const nextTheme = event.matches
        ? 'dark'
        : 'light';

      setThemeState(nextTheme);
    };

    mediaQuery.addEventListener?.(
      'change',
      handleSystemThemeChange
    );

    return () => {
      mediaQuery.removeEventListener?.(
        'change',
        handleSystemThemeChange
      );
    };
  }, []);

  const setTheme = (nextTheme) => {
    if (
      nextTheme !== 'light' &&
      nextTheme !== 'dark'
    ) {
      return;
    }

    setThemeState(nextTheme);

    localStorage.setItem(
      'barterfi:theme',
      nextTheme
    );
  };

  const toggleTheme = () => {
    setThemeState((currentTheme) => {
      const nextTheme =
        currentTheme === 'dark'
          ? 'light'
          : 'dark';

      localStorage.setItem(
        'barterfi:theme',
        nextTheme
      );

      return nextTheme;
    });
  };

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
    }),
    [theme]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
